/**
 * config/config.js — single source of truth for environment & runtime settings.
 *
 * To point the whole frontend at a real backend, change values HERE (or inject
 * them at runtime via `window.__PROMPTO_ENV__ = {...}` before this module loads,
 * or at build time). Nothing else in the codebase needs editing.
 *
 * ES module. Import:  import CONFIG from "../config/config.js";
 */

const ENV = (typeof window !== "undefined" && window.__PROMPTO_ENV__) || {};
const VITE_ENV = (typeof import.meta !== "undefined" && import.meta.env) || {};
const ORIGIN = typeof location !== "undefined" ? location.origin : "";
const pick = (k, fallback) => {
  if (ENV[k] !== undefined) return ENV[k];
  const viteKey = "VITE_" + k;
  return VITE_ENV[viteKey] !== undefined ? VITE_ENV[viteKey] : fallback;
};
const pickBool = (k, fallback) => {
  const value = pick(k, fallback);
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["false", "0", "no", "off"].includes(normalized)) return false;
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    return Boolean(fallback);
  }
  return Boolean(value);
};

export const CONFIG = {
  /* ── Backend (internal API) ─────────────────────────────── */
  // REST root for our own backend. In prod set to e.g. "https://api.prompto.io/v1".
  API_BASE_URL: pick("API_BASE_URL", "/api/v1"),
  API_TIMEOUT_MS: pick("API_TIMEOUT_MS", 15000),

  /* ── Mock mode ──────────────────────────────────────────────
   * true  → api.js serves data from js/*_data.json (NO backend required).
   * false → api.js performs real HTTP calls against API_BASE_URL.
   * Flip this single flag to switch the entire app over to the real backend. */
  USE_MOCK: pickBool("USE_MOCK", true),
  MOCK_LATENCY_MS: pick("MOCK_LATENCY_MS", 320),   // simulated network delay
  MOCK_DATA_BASE: pick("MOCK_DATA_BASE", "/data"),  // served from public/data in Vite

  /* ── Auth / session ─────────────────────────────────────── */
  TOKEN_STORAGE_KEY: "prompto.auth.token",
  USER_STORAGE_KEY: "prompto.auth.user",
  TOKEN_HEADER: "Authorization",
  TOKEN_SCHEME: "Bearer",

  /* ── OAuth (external identity providers) ────────────────────
   * Fill clientId/redirectUri per environment. See js/api_external.js. */
  OAUTH: {
    google: {
      clientId: pick("GOOGLE_CLIENT_ID", "<YOUR_GOOGLE_CLIENT_ID>.apps.googleusercontent.com"),
      redirectUri: pick("GOOGLE_REDIRECT_URI", ORIGIN + "/auth/google/callback"),
      scope: "openid email profile",
      authBase: "https://accounts.google.com/o/oauth2/v2/auth",
      gisSrc: "https://accounts.google.com/gsi/client",
    },
    kakao: {
      clientId: pick("KAKAO_REST_KEY", "<YOUR_KAKAO_REST_API_KEY>"),
      jsKey: pick("KAKAO_JS_KEY", "<YOUR_KAKAO_JAVASCRIPT_KEY>"),
      redirectUri: pick("KAKAO_REDIRECT_URI", ORIGIN + "/auth/kakao/callback"),
      scope: "profile_nickname profile_image account_email",
      authBase: "https://kauth.kakao.com/oauth/authorize",
      sdkSrc: "https://t1.kakaocdn.net/kakao_js_sdk/2.7.2/kakao.min.js",
    },
  },

  /* ── Image generation ───────────────────────────────────── */
  GENERATION_POLL_MS: pick("GENERATION_POLL_MS", 1200),    // status poll interval
  GENERATION_TIMEOUT_MS: pick("GENERATION_TIMEOUT_MS", 120000),

  /* ── Pagination ─────────────────────────────────────────── */
  PAGE_SIZE: pick("PAGE_SIZE", 12),

  /* ── Feature flags ──────────────────────────────────────── */
  FEATURES: {
    generation: pickBool("FEATURE_GENERATION", false),
    social: pickBool("FEATURE_SOCIAL", true),
    download: pickBool("FEATURE_DOWNLOAD", true),
    oauthGoogle: pickBool("FEATURE_OAUTH_GOOGLE", false),
    oauthKakao: pickBool("FEATURE_OAUTH_KAKAO", false),
  },

  /* ── Debug ──────────────────────────────────────────────── */
  DEBUG: pick("DEBUG", false),
};

export default CONFIG;
